// Build a complete, valid LeagueSettings from a sport and a few wizard
// answers. Everything the wizard doesn't ask comes from the sport's first
// presets and sensible defaults; the commissioner can change all of it later.

import type { LeagueFormatId, SportDefinition } from '@fsp/sports-core';
import {
  LeagueSettings,
  SETTINGS_SCHEMA_VERSION,
  type LeagueSettingsInput,
  type ScoringConfig,
  type WaiverSystemAnswer,
} from './schemas';
import { playsTheField } from '../matchup/format';
import { divisionsFor } from './divisions';
import { setupPresets } from './scoring-table';
import { waiverWeekdaysOf } from './validate';

/** Wednesday, the big sites' day, when the sport runs waivers on it; else its first day. */
function defaultWaiverDay(sport: SportDefinition): number {
  const days = waiverWeekdaysOf(sport);
  return days.includes(3) ? 3 : Math.min(...days);
}

/**
 * A new FAAB league's season budget, in dollars that are not money.
 *
 * Exported so the create form's card can say the same number the rules will
 * hold rather than typing it a second time. $100 is Sleeper's and Yahoo's
 * default, and ESPN's by third-party report (WAIVERS_RESEARCH.md).
 */
export const DEFAULT_FAAB_BUDGET = 100;

/**
 * An elimination league's season budget (B-364): a whole roster comes onto
 * waivers every week and the survivors bid on each, so the guillotine
 * leagues' custom is $1,000 for the season.
 */
export const ELIMINATION_FAAB_BUDGET = 1000;

export interface WizardAnswers {
  name: string;
  teamCount: number;
  season: number;
  timeZone: string;
  format?: LeagueFormatId;
  /** A head-to-head league also plays the league median each week (Q3). Absent is no. */
  medianGame?: boolean;
  /** A head-to-head league plays two opponents every week (Q6). Absent is no. */
  twoOpponents?: boolean;
  scoringPresetId?: string;
  /**
   * The answers to the sport's scoring questions (`ScoringSetup`, DIRECTION
   * A12), each absent one at its default. They choose the scoring preset and
   * the roster preset, and win over `scoringPresetId` when both are sent.
   */
  scoringSetup?: Readonly<Record<string, string>>;
  /**
   * The scoring itself, when the commissioner changed a number on the create
   * form (PLAN-2026-10-18 §2.3): the table is the answer to that question.
   * Replaces the preset's scoring; the roster is still the answers'.
   */
  scoring?: ScoringConfig;
  categoryPresetId?: string;
  rosterPresetId?: string;
  /**
   * The roster itself, when the commissioner set it under the create form's
   * More options (PLAN-2026-10-18-families-and-newcomers §4.6): each slot's
   * count and the injured-reserve spots. Replaces the preset's slots; the
   * rounds follow it, a round per spot.
   */
  roster?: { slots: readonly { slot: string; count: number }[]; reserve: number };
  /** How the picks are made: a pick order, or `auction`. Absent is `snake`. */
  draftType?: 'snake' | 'linear' | 'third-round-reversal' | 'auction';
  /** Who is present and who enters it. Absent is `autopick`. */
  draftMode?: 'live' | 'autopick' | 'manual';
  draftScheduledAt?: string | null;
  /** How the managers rank for the autopick draft. Absent is one list. */
  draftRanking?: 'overall' | 'by-position';
  /**
   * The pick clock, in seconds.
   *
   * Here rather than patched on afterwards. The wizard asks for it on the
   * same step as the draft time, and until 2026-09-15 it answered by
   * assigning `settings.draft.pickSeconds` to the object this function had
   * already parsed — which worked on the client and silently dropped the
   * answer the moment the server started building the settings from the
   * same answers. An input the wizard collects belongs in the input type.
   */
  pickSeconds?: number;
  /** The hours a live draft's clock stops, in the league's zone; absent or null for none (C7). */
  overnightPause?: { from: string; to: string } | null;
  /**
   * Managers trade picks and drafted players in a live draft's room
   * (DRAFT_ROOM.md "Trading during the draft"). Absent is no.
   */
  pickTradingAllowed?: boolean;
  /**
   * An auction's choices (AUCTION.md §4), read when `draftType` is
   * `auction`; each absent is its default. A bid clock of an hour or more is a
   * slow auction, which puts several players up at once.
   */
  auction?: {
    budget?: number;
    nominationSeconds?: number;
    bidSeconds?: number;
    lotsAtOnce?: number;
    leftoverToWaivers?: boolean;
  };
  /**
   * The league's first regular-season period, when it is not the sport's.
   *
   * A league made after its sport's season has started plays the weeks still
   * to come, a shorter season from there. Which weeks those are is a fact
   * about the pro
   * schedule, so the server decides it from `games` and hands it in here;
   * this function only honors it. Absent means the sport's own first period.
   */
  firstPeriod?: number;
  /**
   * How waivers work: the worst team claims first, or everyone bids.
   *
   * Absent is `reverse-standings`, the default since 2026-09-24: bidding is
   * an option, never the default.
   * `faab` gives every team `DEFAULT_FAAB_BUDGET` for the season. Carried in
   * this type for the reason `pickSeconds` is: an answer the form collects
   * that does not reach this function is dropped without a sound (B-71).
   */
  waiverSystem?: WaiverSystemAnswer;
  /**
   * How many divisions, under the create form's More options
   * (PLAN-2026-10-29-divisions §4): 0, 2, 3 or 4, each named by
   * `divisionsFor`. Absent is none.
   */
  divisions?: number;
}

/** The auction's own clocks: a live auction's or a slow one's (AUCTION.md §4, §5). */
export const AUCTION_DEFAULTS = {
  budget: 200,
  nominationSeconds: 30,
  bidSeconds: 20,
  resetSeconds: 10,
  slowLotsAtOnce: 3,
} as const;

/** Whether an auction's bid clock makes it a slow one: an hour or more (AUCTION.md §5). */
export const isSlowAuction = (bidSeconds: number): boolean => bidSeconds >= 3600;

function auctionRules(a: WizardAnswers): NonNullable<LeagueSettings['draft']['auction']> {
  const bidSeconds = a.auction?.bidSeconds ?? AUCTION_DEFAULTS.bidSeconds;
  const slow = isSlowAuction(bidSeconds);
  return {
    budget: a.auction?.budget ?? AUCTION_DEFAULTS.budget,
    minBid: 1,
    nominationSeconds: a.auction?.nominationSeconds ?? AUCTION_DEFAULTS.nominationSeconds,
    bidSeconds,
    // A SLOW AUCTION'S LATE BID gives half the clock back, not ten seconds
    // somebody asleep cannot answer in.
    // And never past where the clock starts.
    resetSeconds: slow
      ? Math.round(bidSeconds / 2)
      : Math.min(bidSeconds, AUCTION_DEFAULTS.resetSeconds),
    lotsAtOnce: slow ? (a.auction?.lotsAtOnce ?? AUCTION_DEFAULTS.slowLotsAtOnce) : 1,
    leftoverToWaivers: a.waiverSystem === 'faab' && (a.auction?.leftoverToWaivers ?? false),
  };
}

/** Replace the scoring section with a sport preset (the editor's "start from a preset"). */
export function applyScoringPreset(
  settings: LeagueSettings,
  sport: SportDefinition,
  presetId: string,
): LeagueSettings {
  const preset = sport.scoringPresets.find((p) => p.id === presetId);
  if (!preset) throw new Error(`${sport.id} has no scoring preset ${presetId}`);
  return LeagueSettings.parse({
    ...settings,
    scoring: {
      presetId: preset.id,
      units: preset.config.units.map((u) => ({
        ...u,
        positions: u.positions ? [...u.positions] : null,
      })),
      bonuses: preset.config.bonuses.map((b) => ({
        ...b,
        positions: b.positions ? [...b.positions] : null,
      })),
      tiers: preset.config.tiers.map((t) => ({
        ...t,
        bands: t.bands.map((band) => ({ ...band })),
        positions: t.positions ? [...t.positions] : null,
      })),
      decimals: preset.config.decimals,
    },
  });
}

/** Replace the roster section with a sport preset. */
export function applyRosterPreset(
  settings: LeagueSettings,
  sport: SportDefinition,
  presetId: string,
): LeagueSettings {
  const preset = sport.rosterPresets.find((p) => p.id === presetId);
  if (!preset) throw new Error(`${sport.id} has no roster preset ${presetId}`);
  return LeagueSettings.parse({
    ...settings,
    roster: {
      presetId: preset.id,
      slots: preset.slots.map((s) => ({ ...s })),
      maxPerPosition: { ...(preset.maxPerPosition ?? {}) },
      reserve: preset.reserve
        ? { ...preset.reserve, eligibleStatuses: [...preset.reserve.eligibleStatuses] }
        : null,
      taxi: preset.taxi ? { ...preset.taxi, deadlinePeriod: null } : null,
    },
  });
}

export function buildDefaultSettings(sport: SportDefinition, a: WizardAnswers): LeagueSettings {
  const setup = a.scoringSetup === undefined ? null : setupPresets(sport, a.scoringSetup);
  const scoringPresetId = setup?.scoring ?? a.scoringPresetId;
  const scoringPreset =
    sport.scoringPresets.find((p) => p.id === scoringPresetId) ?? sport.scoringPresets[0] ?? null;
  const categoryPreset =
    sport.categoryPresets.find((p) => p.id === a.categoryPresetId) ??
    sport.categoryPresets[0] ??
    null;
  const rosterPreset =
    sport.rosterPresets.find((p) => p.id === (a.rosterPresetId ?? setup?.roster)) ??
    sport.rosterPresets[0];
  if (!rosterPreset) throw new Error(`${sport.id} has no roster presets`);
  if (!scoringPreset) throw new Error(`${sport.id} has no scoring presets`);

  const format = a.format ?? sport.supportedFormats[0] ?? 'h2h-points';
  // THE COMMISSIONER'S OWN ROSTER, over the preset's: its slots with a count,
  // and a reserve shaped as the preset's (or the sport's injured list) when
  // it has spots.
  const reserveSlot = sport.slots.find((s) => s.kind === 'reserve');
  const ownRoster = a.roster;
  const slots = ownRoster
    ? ownRoster.slots.filter((s) => s.count > 0).map((s) => ({ slot: s.slot, count: s.count }))
    : rosterPreset.slots.map((s) => ({ ...s }));
  const reserve = !ownRoster
    ? rosterPreset.reserve
      ? { ...rosterPreset.reserve, eligibleStatuses: [...rosterPreset.reserve.eligibleStatuses] }
      : null
    : ownRoster.reserve === 0 || reserveSlot === undefined
      ? null
      : rosterPreset.reserve
        ? {
            ...rosterPreset.reserve,
            count: ownRoster.reserve,
            eligibleStatuses: [...rosterPreset.reserve.eligibleStatuses],
          }
        : {
            slot: reserveSlot.id,
            count: ownRoster.reserve,
            eligibleStatuses: sport.injuryStatuses
              .filter((st) => st.severity === 'out')
              .map((st) => st.id),
          };
  const sportSeason = sport.calendar.defaultRegularSeasonPeriods(a.season);
  const reg = { first: a.firstPeriod ?? sportSeason.first, last: sportSeason.last };
  const playoffs = sport.calendar.defaultPlayoffPeriods(a.season);
  const rosterSize = slots.reduce((n, s) => n + s.count, 0);
  const playoffTeams = a.teamCount >= 10 ? 6 : a.teamCount >= 6 ? 4 : 2;
  // A FORMAT WITH NO BRACKET PLAYS ITS WHOLE SEASON (PLAN-2026-10-16 §2):
  // total points and elimination run through the weeks a bracket would have
  // had, as Yahoo's points leagues do, rather than stopping three weeks short.
  const bracket = !(
    format === 'rotisserie' ||
    format === 'points-total' ||
    format === 'elimination'
  );

  const input: LeagueSettingsInput = {
    schemaVersion: SETTINGS_SCHEMA_VERSION,
    sport: sport.id,
    season: a.season,
    identity: { name: a.name, teamCount: a.teamCount, timeZone: a.timeZone },
    format: {
      id: format,
      // THE MEDIAN IS A HEAD-TO-HEAD WEEK'S SECOND GAME: a league against the
      // field has none to add (PLAN-2026-10-16-league-formats §2).
      h2h: {
        medianGame: a.medianGame === true && !playsTheField(format),
        twoOpponents: a.twoOpponents === true && !playsTheField(format),
      },
      elimination: format === 'elimination' ? {} : null,
    },
    // A SCORING SENT WHOLE IS CUSTOM, whatever preset it names (the review
    // of PLAN-2026-10-18 chunk 2): the label is the server's to give.
    scoring: a.scoring
      ? { ...a.scoring, presetId: null }
      : {
          presetId: scoringPreset.id,
          units: scoringPreset.config.units.map((u) => ({
            ...u,
            positions: u.positions ? [...u.positions] : null,
          })),
          bonuses: scoringPreset.config.bonuses.map((b) => ({
            ...b,
            positions: b.positions ? [...b.positions] : null,
          })),
          tiers: scoringPreset.config.tiers.map((t) => ({
            ...t,
            bands: t.bands.map((band) => ({ ...band })),
            positions: t.positions ? [...t.positions] : null,
          })),
          decimals: scoringPreset.config.decimals,
        },
    categories: categoryPreset
      ? {
          presetId: categoryPreset.id,
          categories: categoryPreset.config.categories.map((c) => ({ ...c })),
          ties: categoryPreset.config.ties,
        }
      : null,
    roster: {
      presetId: ownRoster ? null : rosterPreset.id,
      slots,
      maxPerPosition: { ...(rosterPreset.maxPerPosition ?? {}) },
      reserve,
      taxi: rosterPreset.taxi ? { ...rosterPreset.taxi, deadlinePeriod: null } : null,
    },
    lineup: {
      period: sport.defaultLineupPeriod,
      lockRule: sport.defaultLockRule,
      bestBall: format === 'best-ball',
    },
    schedule: {
      regularSeason: {
        firstPeriod: reg.first,
        lastPeriod: bracket ? reg.last : Math.max(reg.last, ...playoffs),
      },
      divisions: divisionsFor(a.divisions ?? 0, []),
    },
    playoffs: {
      teamCount: bracket ? playoffTeams : 0,
      periods: bracket ? [...playoffs] : [],
      byes: bracket && playoffTeams === 6 ? 2 : 0,
    },
    tiebreakers: { standings: ['head-to-head', 'points-for', 'points-against', 'coin-flip'] },
    // THE WORST TEAM CLAIMS FIRST unless the league asked to bid. A budget is
    // the one other answer the form offers, and only then is there a `faab`
    // section: a league that does not bid has no budget to show, and creating
    // it seeds every team's `faab_remaining` from this (null is 0). The
    // schema's defaults fill the rest of a budget (`WaiverSettings`), and the
    // processing time below is the same either way (WAIVERS_RESEARCH.md).
    waivers: {
      system: a.waiverSystem ?? 'reverse-standings',
      faab:
        a.waiverSystem === 'faab'
          ? { budget: format === 'elimination' ? ELIMINATION_FAAB_BUDGET : DEFAULT_FAAB_BUDGET }
          : null,
      // WEDNESDAY, or the sport's first day for a weekly run when it is not
      // one (`calendar.waiverWeekdays`, B-509).
      processing: { schedule: 'weekly', time: '03:00', weekday: defaultWaiverDay(sport) },
    },
    acquisitions: {},
    trades: { review: 'commissioner', deadlinePeriod: Math.max(reg.first, reg.last - 3) },
    draft: {
      format: a.draftType === 'auction' ? 'auction' : 'pick',
      order: a.draftType === 'auction' ? null : (a.draftType ?? 'snake'),
      mode: a.draftMode ?? 'autopick',
      scheduledAt: a.draftScheduledAt ?? null,
      rounds: rosterSize,
      pickSeconds: a.pickSeconds ?? 90,
      // A STOP IS A LIVE CLOCK'S: nothing else has a clock to stop.
      overnightPause: a.draftMode === 'live' ? (a.overnightPause ?? null) : null,
      ranking: a.draftRanking ?? 'overall',
      // TRADES IN THE ROOM ARE A LIVE PICK DRAFT'S: nobody is in a room
      // otherwise, and an auction has no picks to trade (AUCTION.md §3.9).
      pickTradingAllowed:
        a.draftMode === 'live' && a.draftType !== 'auction'
          ? (a.pickTradingAllowed ?? false)
          : false,
      auction: a.draftType === 'auction' ? auctionRules(a) : null,
    },
    keepers: { mode: 'redraft' },
    governance: {},
    fun: {},
    tools: {},
  };

  return LeagueSettings.parse(input);
}
